import React, { useId, useState } from 'react';
import { motion, AnimatePresence, LayoutGroup } from 'framer-motion';

export interface FaqItem {
  question: string;
  answer: string;
}

export interface FaqAccordionProps {
  id?: string;
  title?: string;
  items: FaqItem[];
  defaultOpenIndex?: number | null;
  className?: string;
}

const FaqAccordion: React.FC<FaqAccordionProps> = ({
  id,
  title,
  items,
  defaultOpenIndex = null,
  className = '',
}) => {
  const [openIdx, setOpenIdx] = useState<number | null>(defaultOpenIndex);
  const baseId = useId();

  return (
    <section id={id} className={`scroll-mt-28 ${className}`}>
      {title && (
        <h2 className="text-4xl md:text-5xl font-display font-bold gradient-text mb-10 text-center">
          {title}
        </h2>
      )}
      <LayoutGroup>
        <motion.div layout className="space-y-4">
          {items.map((item, idx) => {
            const isOpen = openIdx === idx;
            const headingId = `${baseId}-faq-h-${idx}`;
            const panelId = `${baseId}-faq-p-${idx}`;

            return (
              <motion.div
                key={idx}
                layout
                transition={{ type: 'spring', stiffness: 320, damping: 30 }}
                className={`overflow-hidden rounded-2xl border border-dark-600/80 bg-dark-800/30 backdrop-blur-sm shadow-lg transition-shadow duration-300 ${
                  isOpen ? 'ring-1 ring-primary-500/35 shadow-primary-500/10' : 'hover:border-dark-500/80'
                }`}
              >
                <button
                  type="button"
                  id={headingId}
                  aria-expanded={isOpen}
                  aria-controls={panelId}
                  onClick={() => setOpenIdx(isOpen ? null : idx)}
                  className="group flex w-full items-center justify-between gap-4 p-5 md:p-6 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/60 focus-visible:ring-offset-2 focus-visible:ring-offset-dark-900 rounded-2xl"
                >
                  <span className="text-lg md:text-xl font-display font-semibold text-purple-100/95 pr-2">
                    {item.question}
                  </span>
                  <motion.span
                    animate={{ rotate: isOpen ? 180 : 0 }}
                    transition={{ duration: 0.22, ease: 'easeOut' }}
                    className="shrink-0 inline-flex h-9 w-9 items-center justify-center rounded-xl border border-primary-500/25 bg-primary-500/10 text-primary-300"
                    aria-hidden
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </motion.span>
                </button>

                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      id={panelId}
                      role="region"
                      aria-labelledby={headingId}
                      key="content"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.28, ease: [0.4, 0, 0.2, 1] }}
                      className="overflow-hidden border-t border-dark-600/50"
                    >
                      <div className="px-5 pb-5 pt-4 md:px-6 md:pb-6 text-dark-200 leading-relaxed text-[15px] md:text-base">
                        {item.answer}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </motion.div>
      </LayoutGroup>
    </section>
  );
};

export default FaqAccordion;
